/**
 * aggregateOverview — pure derivation. No React, no async.
 *
 * Consumes the four raw backend responses (analysis results,
 * workflow status, UN R155 checklist, V&V recommendations) and
 * returns the `AggregatedOverviewData` consumed by every card.
 *
 * Pure-function shape means:
 *   • Easy unit-testable against fixture `getAnalysisResults` /
 *     `getWorkflowStatus` payloads (see `aggregateOverview.test.ts`).
 *   • Cheap to re-run on each polling tick via `useMemo` in the
 *     hook layer — short-circuits when inputs are reference-stable.
 *   • Cards stay pure: they index into the slice they care about,
 *     never re-derive.
 *
 * Tolerant of missing data — every step's records may be absent
 * until the user reaches that step. The output is always a fully-
 * populated `AggregatedOverviewData`; absent records collapse to
 * zero counts / null slices.
 */

import { deriveStatus } from '../../athena/state/workflowV2Reducer';
import { STEP_CATALOG } from '../../athena/state/workflowV2StepCatalog';
import type { StepStatus } from '../../athena/state/workflowV2Types';
import {
  AggregatedOverviewData,
  AttackPathRow,
  CalEffortRow,
  CalLevel,
  ChildTaraSummary,
  CoverageEdge,
  EMPTY_AGGREGATE,
  Iso21434ClauseRow,
  OrphanFinding,
  RiskDistributionSlice,
  RiskTreatmentMatrixSlice,
  Severity,
  StrideKey,
  StrideSlice,
  TreatmentKey,
  TreatmentMixSlice,
  Unr155Slice,
  UntreatedRisksSlice,
} from './types';

// ── Inputs ──────────────────────────────────────────────────────

export interface AggregateInput {
  analysis: any | null;          // GET /api/v1/analysis/{tid}/results
  workflow: any | null;          // GET /workflow/{tid}/status
  unr155: any | null;            // GET /api/v1/session/{sid}/unr155-checklist
  vvRecommendations: any | null; // GET /api/v1/vv-recommendations/{tid}
  analysisMode: 'individual' | 'hierarchical' | null;
}

// ── Normalisation helpers ───────────────────────────────────────

/* Accepts either a severity string (Critical / High / Medium / Low /
   Negligible / Moderate) OR the ISO 21434 risk-matrix integer 1..5
   that `RiskAssessmentResponse.risk_value` actually emits. Without
   the numeric branch every risk falls through to `null` and the
   Risk Distribution / Top Attack Paths / Risk Score cards all read
   as if there were zero risks. */
const normSeverity = (s: unknown): Severity | null => {
  if (typeof s === 'number') {
    if (s >= 5) return 'critical';
    if (s === 4) return 'high';
    if (s === 3) return 'medium';
    if (s >= 1) return 'low';
    return null;
  }
  const v = typeof s === 'string' ? s.toLowerCase().trim() : '';
  if (!v) return null;
  // Numeric strings ("5", "4", …) come through as strings on some
  // serialisation paths — coerce + bucket the same way.
  if (/^[1-5](\.\d+)?$/.test(v)) return normSeverity(Number(v));
  if (v.includes('critical')) return 'critical';
  if (v.includes('high')) return 'high';
  if (v.includes('medium') || v.includes('moderate')) return 'medium';
  if (v.includes('low') || v.includes('negligible')) return 'low';
  return null;
};

const normTreatment = (t: unknown): keyof TreatmentMixSlice | null => {
  const v = typeof t === 'string' ? t.toLowerCase() : '';
  if (v.includes('reduce') || v.includes('mitigate')) return 'reduce';
  if (v.includes('share') || v.includes('transfer')) return 'share';
  if (v.includes('retain') || v.includes('accept')) return 'retain';
  if (v.includes('avoid')) return 'avoid';
  return null;
};

const normStride = (s: unknown): StrideKey | null => {
  const v = typeof s === 'string' ? s.toUpperCase().replace(/[\s-]/g, '_') : '';
  if (v.includes('SPOOF')) return 'spoofing';
  if (v.includes('TAMPER')) return 'tampering';
  if (v.includes('REPUDIATION')) return 'repudiation';
  if (v.includes('INFORMATION')) return 'information_disclosure';
  if (v.includes('DENIAL')) return 'denial_of_service';
  if (v.includes('ELEVATION')) return 'elevation_of_privilege';
  return null;
};

const normCal = (c: unknown): CalLevel | null => {
  // Strip separators first: agents emit "CAL2", "CAL 3", "cal-4" variants.
  const v =
    typeof c === 'string'
      ? c.toUpperCase().replace(/[\s_-]/g, '')
      : typeof c === 'number'
        ? `CAL${c}`
        : '';
  if (v.includes('CAL4') || v === '4') return 'CAL4';
  if (v.includes('CAL3') || v === '3') return 'CAL3';
  if (v.includes('CAL2') || v === '2') return 'CAL2';
  if (v.includes('CAL1') || v === '1') return 'CAL1';
  return null;
};

const pickArray = (...candidates: unknown[]): any[] => {
  for (const c of candidates) {
    if (Array.isArray(c)) return c;
  }
  return [];
};

// ── Per-card derivations ────────────────────────────────────────

const deriveStepStatuses = (workflow: any): StepStatus[] => {
  // STEP_CATALOG includes Step 0; Overview banner cares about 1..8.
  const agentProgress = (workflow?.agent_progress ?? workflow?.agent_details ?? {}) as Record<
    string,
    { status?: string; review_status?: string }
  >;
  return STEP_CATALOG.filter((s) => s.id >= 1 && s.id <= 8).map((s) => {
    const ap = s.agent ? agentProgress[s.agent] : undefined;
    return deriveStatus(ap);
  });
};

const deriveStatusBanner = (
  input: AggregateInput,
  counts: AggregatedOverviewData['statusBanner']['counts'],
): AggregatedOverviewData['statusBanner'] => {
  const { workflow, analysisMode } = input;
  const stepStatuses = deriveStepStatuses(workflow);
  const phaseRaw = (workflow?.phase ?? workflow?.status ?? '').toLowerCase();
  const phase: AggregatedOverviewData['statusBanner']['phase'] = phaseRaw.includes('complete')
    ? 'completed'
    : phaseRaw.includes('fail')
      ? 'failed'
      : phaseRaw.includes('progress') || phaseRaw.includes('running') || phaseRaw.includes('paused')
        ? 'in_progress'
        : 'not_started';

  // First non-approved step is the user's "current" focus. Falls back to 1.
  const firstUnapproved = stepStatuses.findIndex((s) => s !== 'approved');
  const currentStep = firstUnapproved === -1 ? 8 : firstUnapproved + 1;

  const lastActivity =
    workflow?.last_updated ?? workflow?.updated_at ?? workflow?.completed_at ?? null;

  const importedChildTaras =
    typeof workflow?.imported_child_taras === 'number' ? workflow.imported_child_taras : 0;

  return {
    phase,
    currentStep,
    stepStatuses,
    lastActivity,
    analysisMode,
    importedChildTaras,
    counts,
  };
};

const deriveTreatmentMix = (risks: any[]): TreatmentMixSlice => {
  const mix: TreatmentMixSlice = { reduce: 0, share: 0, retain: 0, avoid: 0 };
  for (const r of risks) {
    const t = normTreatment(r?.treatment_decision ?? r?.treatment ?? r?.risk_treatment);
    if (t) mix[t] += 1;
  }
  return mix;
};

const deriveUntreatedRisks = (risks: any[]): UntreatedRisksSlice => {
  let critical = 0;
  let high = 0;
  for (const r of risks) {
    const sev = normSeverity(r?.final_risk_value ?? r?.risk_value ?? r?.risk_level ?? r?.severity);
    const t = normTreatment(r?.treatment_decision ?? r?.treatment ?? r?.risk_treatment);
    if (!t) {
      if (sev === 'critical') critical += 1;
      else if (sev === 'high') high += 1;
    }
  }
  return { count: critical + high, critical, high };
};

const deriveRiskDistribution = (risks: any[]): RiskDistributionSlice => {
  const dist: RiskDistributionSlice = { critical: 0, high: 0, medium: 0, low: 0 };
  for (const r of risks) {
    const sev = normSeverity(r?.final_risk_value ?? r?.risk_value ?? r?.risk_level ?? r?.severity);
    if (sev) dist[sev] += 1;
  }
  return dist;
};

/* 4×4 joint distribution — severity × treatment decision. Feeds the
   combined heat-map card that replaced the separate Risk Distribution
   and Treatment Decisions cards. */
const deriveRiskTreatmentMatrix = (risks: any[]): RiskTreatmentMatrixSlice => {
  const emptyRow = (): Record<TreatmentKey, number> => ({
    reduce: 0,
    share: 0,
    retain: 0,
    avoid: 0,
  });
  const cells: RiskTreatmentMatrixSlice['cells'] = {
    critical: emptyRow(),
    high: emptyRow(),
    medium: emptyRow(),
    low: emptyRow(),
  };
  let total = 0;
  let untreated = 0;
  for (const r of risks) {
    const sev = normSeverity(r?.final_risk_value ?? r?.risk_value ?? r?.risk_level ?? r?.severity);
    if (!sev) continue;
    const t = normTreatment(r?.treatment_decision ?? r?.treatment ?? r?.risk_treatment);
    if (!t) {
      untreated += 1;
      continue;
    }
    cells[sev][t] += 1;
    total += 1;
  }
  const max = Math.max(0, ...Object.values(cells).flatMap((row) => Object.values(row)));
  return { cells, total, max, untreated };
};

const deriveRiskScore = (dist: RiskDistributionSlice): number => {
  // Severity-weighted average: Critical=10, High=7, Medium=4, Low=1.
  const total = dist.critical + dist.high + dist.medium + dist.low;
  if (total === 0) return 0;
  const weighted = dist.critical * 10 + dist.high * 7 + dist.medium * 4 + dist.low * 1;
  return Math.round((weighted / total) * 10) / 10;
};

const deriveStride = (threats: any[]): StrideSlice => {
  const s: StrideSlice = {
    spoofing: 0,
    tampering: 0,
    repudiation: 0,
    information_disclosure: 0,
    denial_of_service: 0,
    elevation_of_privilege: 0,
  };
  for (const t of threats) {
    const key = normStride(t?.stride_category ?? t?.category);
    if (key) s[key] += 1;
  }
  return s;
};

const deriveIso21434 = (workflow: any): Iso21434ClauseRow[] => {
  const agentProgress = (workflow?.agent_progress ?? workflow?.agent_details ?? {}) as Record<
    string,
    { status?: string; review_status?: string }
  >;
  return STEP_CATALOG.filter((s) => s.id >= 1 && s.id <= 8).map((s) => {
    const ap = s.agent ? agentProgress[s.agent] : undefined;
    const clauseRef = (s.clause ?? '').replace(/^ISO 21434 §/, '');
    return {
      ref: clauseRef || '—',
      name: s.title,
      status: deriveStatus(ap),
    };
  });
};

/* An item counts as "classified" once the user made an explicit relevance
   decision — `relevant` OR `not_relevant`. `unset` is the only unclassified
   state. (The old substring filter counted "not_relevant" because it
   contains the word "relevant", and read category fields that don't exist
   on the checklist response.) */
const isClassified = (relevance: unknown): boolean => {
  const v = typeof relevance === 'string' ? relevance.toLowerCase() : '';
  return v === 'relevant' || v === 'not_relevant';
};

const deriveUnr155 = (unr155: any): Unr155Slice | null => {
  if (!unr155) return null;

  // Real checklist shape: { session_id, threats: [category…], completion_stats }
  // where completion_stats = { total, relevant, not_relevant, unset } and each
  // category = { category_title, section, items: [{ item_id, relevance, … }] }.
  const categories = pickArray(unr155.threats, unr155.categories, unr155.sections);
  const sections = categories.map((cat: any) => {
    const items = pickArray(cat.items, cat.controls);
    const classified = items.filter((i: any) => isClassified(i?.relevance)).length;
    const total = items.length;
    const pct = total > 0 ? Math.round((classified / total) * 100) : 0;
    return {
      name: cat.category_title ?? cat.name ?? cat.title ?? 'Section',
      pct,
      classified,
      total,
    };
  });

  const stats = unr155.completion_stats ?? unr155.stats ?? null;
  let overallPct = 0;
  if (typeof stats?.completion_percentage === 'number') {
    // Forward-compatible: honor an explicit percentage if the backend adds one.
    overallPct = Math.round(stats.completion_percentage);
  } else if (typeof stats?.total === 'number' && stats.total > 0) {
    const unset = typeof stats.unset === 'number' ? stats.unset : 0;
    overallPct = Math.round(((stats.total - unset) / stats.total) * 100);
  } else {
    // No stats — compute from the items themselves.
    const total = sections.reduce((n, s) => n + s.total, 0);
    const classified = sections.reduce((n, s) => n + s.classified, 0);
    overallPct = total > 0 ? Math.round((classified / total) * 100) : 0;
  }

  return { overallPct, sections };
};

/* ── Shared link resolution ──
   The coverage chain and the orphan findings are two views of the SAME
   link facts — a coverage gap IS an orphan. Both derive from these
   helpers so the two cards can never disagree. Link fields the backend
   actually emits:
     scenarios.asset_ids[] → assets.asset_id
     threats.damage_ids[]  → scenarios.damage_id
     attackPaths.threat_id → threats.threat_id
     risks.threat_id       → attackPaths.threat_id (risks carry no attack_path_id)
     goals.risk_ids[]      → risks.risk_id
     controls.goal_id      → goals.goal_id
     vv.requirement_id     → requirements.req_id */
const idSet = (items: any[], ...fields: string[]): Set<string> => {
  const out = new Set<string>();
  for (const item of items) {
    for (const f of fields) {
      const v = item?.[f];
      if (Array.isArray(v)) {
        for (const x of v) if (x != null && x !== '') out.add(String(x));
      } else if (v != null && v !== '') {
        out.add(String(v));
      }
    }
  }
  return out;
};

interface LinkRefs {
  scenarioAssetRefs: Set<string>;
  threatDamageRefs: Set<string>;
  pathThreatRefs: Set<string>;
  riskThreatRefs: Set<string>;
  goalRiskRefs: Set<string>;
  controlGoalRefs: Set<string>;
  vvReqRefs: Set<string>;
}

const buildLinkRefs = (
  scenarios: any[],
  threats: any[],
  attackPaths: any[],
  risks: any[],
  goals: any[],
  controls: any[],
  vv: any[],
): LinkRefs => ({
  scenarioAssetRefs: idSet(scenarios, 'asset_ids', 'asset_id'),
  threatDamageRefs: idSet(threats, 'damage_ids', 'damage_scenario_id', 'damage_id'),
  pathThreatRefs: idSet(attackPaths, 'threat_id'),
  riskThreatRefs: idSet(risks, 'threat_id', 'attack_path_id'),
  goalRiskRefs: idSet(goals, 'risk_ids', 'risk_id'),
  controlGoalRefs: idSet(controls, 'goal_id'),
  vvReqRefs: idSet(vv, 'requirement_id', 'req_id'),
});

const deriveCoverage = (
  assets: any[],
  scenarios: any[],
  threats: any[],
  attackPaths: any[],
  requirements: any[],
  risks: any[],
  goals: any[],
  refs: LinkRefs,
): CoverageEdge[] => {
  // Every edge is "% of upstream items referenced by at least one
  // downstream record" — bounded 0..100 by construction (the previous
  // formula divided downstream link-carriers by the upstream count,
  // which exceeded 100% whenever fan-out > 1).
  const pctCovered = (upstream: any[], upstreamIdField: string, referenced: Set<string>) => {
    if (upstream.length === 0) return 0;
    const covered = upstream.filter((u: any) => {
      const id = u?.[upstreamIdField] ?? u?.id;
      return id != null && referenced.has(String(id));
    }).length;
    return Math.round((covered / upstream.length) * 100);
  };

  const {
    scenarioAssetRefs,
    threatDamageRefs,
    pathThreatRefs,
    riskThreatRefs,
    goalRiskRefs,
    controlGoalRefs,
    vvReqRefs,
  } = refs;

  return [
    { from: 'Assets', to: 'Scenarios', pct: pctCovered(assets, 'asset_id', scenarioAssetRefs) },
    { from: 'Scenarios', to: 'Threats', pct: pctCovered(scenarios, 'damage_id', threatDamageRefs) },
    { from: 'Threats', to: 'Attack Paths', pct: pctCovered(threats, 'threat_id', pathThreatRefs) },
    {
      from: 'Attack Paths',
      to: 'Risks',
      // Risks reference threats, not attack paths — a path is "covered"
      // when a risk exists for its threat.
      pct: pctCovered(attackPaths, 'threat_id', riskThreatRefs),
    },
    { from: 'Risks', to: 'Goals', pct: pctCovered(risks, 'risk_id', goalRiskRefs) },
    { from: 'Goals', to: 'Controls', pct: pctCovered(goals, 'goal_id', controlGoalRefs) },
    {
      from: 'Requirements',
      to: 'V&V',
      pct: pctCovered(requirements, 'req_id', vvReqRefs),
    },
  ];
};

const deriveTopAttackPaths = (
  attackPaths: any[],
  risks: any[],
  assets: any[],
): AttackPathRow[] => {
  const SEV_RANK: Record<Severity, number> = { critical: 4, high: 3, medium: 2, low: 1 };
  const assetById = new Map<string, string>();
  for (const a of assets) {
    const k = a?.asset_id ?? a?.id;
    if (k) assetById.set(String(k), a?.name ?? a?.asset_name ?? String(k));
  }
  // Risks reference threats (risk.threat_id), not attack paths — join via
  // the shared threat. Keep attack_path_id as a secondary key for any
  // legacy payloads that carry it.
  const riskByThreatId = new Map<string, any>();
  const riskByPathId = new Map<string, any>();
  for (const r of risks) {
    const tk = r?.threat_id ?? r?.threatId;
    if (tk && !riskByThreatId.has(String(tk))) riskByThreatId.set(String(tk), r);
    const pk = r?.attack_path_id ?? r?.attackPathId;
    if (pk) riskByPathId.set(String(pk), r);
  }
  const rows: AttackPathRow[] = attackPaths.map((p: any) => {
    const r =
      riskByThreatId.get(String(p?.threat_id ?? '')) ??
      riskByPathId.get(String(p?.attack_path_id ?? p?.id ?? ''));
    const sev = normSeverity(r?.final_risk_value ?? r?.risk_value ?? r?.risk_level);
    const treatment = normTreatment(r?.treatment_decision ?? r?.treatment);
    const feasibilityRaw = (p?.attack_feasibility_rating ?? p?.feasibility ?? '').toString();
    const fLower = feasibilityRaw.toLowerCase();
    const feasibility: AttackPathRow['feasibility'] = fLower.includes('high')
      ? 'High'
      : fLower.includes('medium')
        ? 'Medium'
        : fLower.includes('very')
          ? 'Very Low'
          : fLower.includes('low')
            ? 'Low'
            : null;
    return {
      id: String(p?.attack_path_id ?? p?.id ?? ''),
      asset: assetById.get(String(p?.asset_id ?? '')) ?? p?.asset_name ?? '—',
      risk: sev ?? 'low',
      treatment,
      feasibility,
    };
  });
  rows.sort((a, b) => SEV_RANK[b.risk] - SEV_RANK[a.risk]);
  return rows.slice(0, 5);
};

/* Orphan findings — one check per coverage edge, driven by the SAME
   LinkRefs the coverage chain uses, so every sub-100% edge produces
   matching findings here. Uncapped: silently truncating audit findings
   made the card disagree with the chain (the old version stopped at 5
   and skipped two edges entirely). */
const deriveOrphans = (
  assets: any[],
  scenarios: any[],
  threats: any[],
  attackPaths: any[],
  risks: any[],
  goals: any[],
  requirements: any[],
  refs: LinkRefs,
): OrphanFinding[] => {
  const orphans: OrphanFinding[] = [];

  const push = (
    type: OrphanFinding['type'],
    id: string,
    target: string,
    desc: string,
    step: number,
  ) => orphans.push({ id: `${type}:${id}`, type, target, desc, step });

  // Assets with no damage scenario (edge: Assets → Scenarios).
  for (const a of assets) {
    const id = String(a?.asset_id ?? a?.id ?? '');
    if (id && !refs.scenarioAssetRefs.has(id)) {
      push('asset', id, a?.name ?? a?.asset_name ?? id, 'No damage scenarios linked', 3);
    }
  }

  // Scenarios with no threat (edge: Scenarios → Threats).
  for (const s of scenarios) {
    const id = String(s?.damage_id ?? s?.id ?? '');
    if (id && !refs.threatDamageRefs.has(id)) {
      push('scenario', id, id, 'No threat scenarios linked', 4);
    }
  }

  // Threats with no attack path (edge: Threats → Attack Paths).
  for (const t of threats) {
    const id = String(t?.threat_id ?? t?.id ?? '');
    if (id && !refs.pathThreatRefs.has(id)) {
      push('threat', id, id, 'No attack path defined', 5);
    }
  }

  // Attack paths whose threat has no risk (edge: Attack Paths → Risks).
  for (const p of attackPaths) {
    const id = String(p?.attack_path_id ?? p?.id ?? '');
    const threatId = String(p?.threat_id ?? '');
    if (id && threatId && !refs.riskThreatRefs.has(threatId)) {
      push('attack_path', id, id, 'No risk assessed for this path', 6);
    }
  }

  // Risks with no goal (edge: Risks → Goals).
  for (const r of risks) {
    const id = String(r?.risk_id ?? r?.id ?? '');
    if (id && !refs.goalRiskRefs.has(id)) {
      push('risk', id, id, 'No cybersecurity goal linked', 7);
    }
  }

  // Goals with no control (edge: Goals → Controls).
  for (const g of goals) {
    const id = String(g?.goal_id ?? g?.id ?? '');
    if (id && !refs.controlGoalRefs.has(id)) {
      push('goal', id, id, 'No control linked', 8);
    }
  }

  // Requirements with no V&V (edge: Requirements → V&V).
  for (const req of requirements) {
    const id = String(req?.req_id ?? req?.requirement_id ?? req?.id ?? '');
    if (id && !refs.vvReqRefs.has(id)) {
      push('requirement', id, id, 'No V&V recommendation', 8);
    }
  }

  return orphans;
};

const deriveInheritance = (
  analysisMode: AggregateInput['analysisMode'],
  workflow: any,
): ChildTaraSummary[] | null => {
  if (analysisMode !== 'hierarchical') return null;
  const children = pickArray(workflow?.child_taras, workflow?.imported_child_taras_detail);
  return children.map((c: any) => {
    const records = typeof c?.record_count === 'number' ? c.record_count : 0;
    const relevant = typeof c?.relevant_count === 'number' ? c.relevant_count : records;
    return {
      name: c?.name ?? c?.project_name ?? 'Child TARA',
      records,
      relevant,
      status: deriveStatus(c?.progress ?? null),
    };
  });
};

const deriveCalEffort = (controls: any[], requirements: any[]): CalEffortRow[] => {
  // Source-of-truth ordering for the card.
  const desc: Record<CalLevel, string> = {
    CAL1: 'Same team + basic testing',
    CAL2: 'Different person + structured testing',
    CAL3: 'Different team + semi-formal verification',
    CAL4: 'Third-party audit + formal verification',
  };
  const counts: Record<CalLevel, number> = { CAL1: 0, CAL2: 0, CAL3: 0, CAL4: 0 };
  const tally = (items: any[]) => {
    for (const i of items) {
      const k = normCal(i?.cal_level ?? i?.CAL ?? i?.cal);
      if (k) counts[k] += 1;
    }
  };
  tally(controls);
  if (controls.length === 0) tally(requirements); // requirements carry CAL when controls absent
  const total = counts.CAL1 + counts.CAL2 + counts.CAL3 + counts.CAL4;
  return (['CAL1', 'CAL2', 'CAL3', 'CAL4'] as CalLevel[]).map((level) => ({
    level,
    count: counts[level],
    pct: total > 0 ? Math.round((counts[level] / total) * 100) : 0,
    desc: desc[level],
  }));
};

// ── Top-level ───────────────────────────────────────────────────

export function aggregateOverview(input: AggregateInput): AggregatedOverviewData {
  // No analysis data yet → fully-empty aggregate (still respects mode + workflow).
  if (!input.analysis && !input.workflow) {
    return {
      ...EMPTY_AGGREGATE,
      statusBanner: {
        ...EMPTY_AGGREGATE.statusBanner,
        analysisMode: input.analysisMode,
      },
      inheritance: input.analysisMode === 'hierarchical' ? [] : null,
    };
  }

  const a = input.analysis ?? {};
  const assets = pickArray(a.assets, a.criticalAssets);
  const scenarios = pickArray(a.damageScenarios, a.damage_scenarios);
  const threats = pickArray(a.threats);
  const attackPaths = pickArray(a.attackPaths, a.attack_paths);
  const risks = pickArray(a.risks, a.risk_assessments, a.riskAssessments);
  const goals = pickArray(a.cybersecurityGoals, a.goals);
  const claims = pickArray(a.cybersecurityClaims, a.claims);
  const controls = pickArray(a.cybersecurityControls, a.controls);
  const requirements = pickArray(a.requirements, a.cybersecurityRequirements);
  const vv = pickArray(input.vvRecommendations?.recommendations, input.vvRecommendations);

  const counts = {
    assets: assets.length,
    damageScenarios: scenarios.length,
    threats: threats.length,
    attackPaths: attackPaths.length,
    risks: risks.length,
    goals: goals.length,
    claims: claims.length,
    controls: controls.length,
    requirements: requirements.length,
    vvRecommendations: vv.length,
  };

  const statusBanner = deriveStatusBanner(input, counts);
  const treatmentMix = deriveTreatmentMix(risks);
  const untreatedRisks = deriveUntreatedRisks(risks);
  const riskDistribution = deriveRiskDistribution(risks);
  const riskTreatmentMatrix = deriveRiskTreatmentMatrix(risks);
  const riskScore = deriveRiskScore(riskDistribution);
  const stride = deriveStride(threats);
  const iso21434 = deriveIso21434(input.workflow);
  const unr155 = deriveUnr155(input.unr155);
  // One set of link facts feeds BOTH the coverage chain and the orphan
  // findings — they are two views of the same traceability state.
  const linkRefs = buildLinkRefs(scenarios, threats, attackPaths, risks, goals, controls, vv);
  const coverage = deriveCoverage(
    assets,
    scenarios,
    threats,
    attackPaths,
    requirements,
    risks,
    goals,
    linkRefs,
  );
  const topAttackPaths = deriveTopAttackPaths(attackPaths, risks, assets);
  const orphans = deriveOrphans(
    assets,
    scenarios,
    threats,
    attackPaths,
    risks,
    goals,
    requirements,
    linkRefs,
  );
  const inheritance = deriveInheritance(input.analysisMode, input.workflow);
  const calEffort = deriveCalEffort(controls, requirements);

  return {
    statusBanner,
    treatmentMix,
    untreatedRisks,
    riskScore,
    riskDistribution,
    riskTreatmentMatrix,
    iso21434,
    unr155,
    coverage,
    stride,
    topAttackPaths,
    orphans,
    inheritance,
    calEffort,
  };
}
